import { useState, useEffect, useCallback, useRef } from 'react'
import Box from '@mui/material/Box'

const GAME_WIDTH = 800
const GAME_HEIGHT = 400
const GROUND_Y = 340
const GRAVITY = 0.6
const JUMP_FORCE = -13
const SCROLL_SPEED = 3

const LAVA_PIT_WIDTH = 80
const JETPACK_DURATION = 1200 // 20 seconds at 60fps
const JETPACK_FLY_SPEED = 4
const PORTAL_RADIUS = 25
const BULLET_SPEED = 12
const BOULDER_SPEED = 4
const ZOMBIE_SHOOT_INTERVAL = 90 // frames between boulder throws

function generatePortals(startX) {
  const portals = []
  let x = startX
  for (let i = 0; i < 3; i++) {
    x += 800 + Math.random() * 600
    const y = 200 + Math.random() * 80
    portals.push({ x, y })
  }
  return portals
}

function generatePits(startX) {
  const pits = []
  let x = startX
  for (let i = 0; i < 10; i++) {
    x += 300 + Math.random() * 250
    pits.push({ x, width: LAVA_PIT_WIDTH + Math.random() * 40 })
  }
  return pits
}

const JetJump = () => {
  const [gameState, setGameState] = useState('idle') // idle, running, dead
  const [, setScore] = useState(0)
  const [highScore, setHighScore] = useState(0)
  const playerX = useRef(150)
  const playerY = useRef(GROUND_Y)
  const velocityY = useRef(0)
  const isJumping = useRef(false)
  const scrollOffset = useRef(0)
  const pits = useRef([])
  const portals = useRef([])
  const jetpackTimer = useRef(0)
  const keysDown = useRef({})
  const bullets = useRef([]) // { x, y, vx, vy }
  const zombies = useRef([]) // { x, y, shootTimer }
  const boulders = useRef([]) // { x, y, vx, vy }
  const smokes = useRef([]) // { x, y, timer }
  const frameRef = useRef(null)
  const gameStateRef = useRef('idle')
  const canvasRef = useRef(null)

  const startGame = useCallback(() => {
    playerX.current = 150
    playerY.current = GROUND_Y
    velocityY.current = 0
    isJumping.current = false
    scrollOffset.current = 0
    pits.current = generatePits(200)
    portals.current = generatePortals(400)
    jetpackTimer.current = 0
    keysDown.current = {}
    bullets.current = []
    zombies.current = []
    boulders.current = []
    smokes.current = []
    setScore(0)
    setGameState('running')
    gameStateRef.current = 'running'
  }, [])

  const die = useCallback(() => {
    setGameState('dead')
    gameStateRef.current = 'dead'
    const finalScore = Math.floor(scrollOffset.current / 10)
    setScore(finalScore)
    setHighScore((prev) => Math.max(prev, finalScore))
  }, [])

  // keyboard controls
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code === 'Space' || e.code === 'ArrowUp') {
        e.preventDefault()
        if (gameStateRef.current !== 'running') {
          startGame()
          return
        }
        if (jetpackTimer.current <= 0 && !isJumping.current) {
          velocityY.current = JUMP_FORCE
          isJumping.current = true
        }
      }
      if (e.code === 'KeyC' && gameStateRef.current === 'running' && jetpackTimer.current > 0) {
        // fire gun forward and slightly down
        bullets.current.push({
          x: playerX.current,
          y: playerY.current - 18,
          vx: BULLET_SPEED,
          vy: 2,
        })
      }
      if (e.code === 'ArrowUp' || e.code === 'ArrowDown') {
        e.preventDefault()
        keysDown.current[e.code] = true
      }
    }
    const onKeyUp = (e) => {
      if (e.code === 'ArrowUp' || e.code === 'ArrowDown') {
        keysDown.current[e.code] = false
      }
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('keyup', onKeyUp)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('keyup', onKeyUp)
    }
  }, [startGame])

  // game loop
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')

    const toScreen = (worldX) => worldX - scrollOffset.current

    const drawBackground = () => {
      // dungeon walls
      const grad = ctx.createLinearGradient(0, 0, 0, GAME_HEIGHT)
      grad.addColorStop(0, '#1a1a1a')
      grad.addColorStop(1, '#2d2d2d')
      ctx.fillStyle = grad
      ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT)

      // stone brick pattern on ceiling
      ctx.fillStyle = '#3a3a3a'
      for (let i = 0; i < 12; i++) {
        const bx = (i * 70 - (scrollOffset.current * 0.3) % 70 + 70) % (GAME_WIDTH + 70) - 35
        ctx.fillRect(bx, 0, 65, 25)
        ctx.strokeStyle = '#222'
        ctx.lineWidth = 2
        ctx.strokeRect(bx, 0, 65, 25)
      }

      // torch lights (flickering)
      const flicker = Math.random() * 0.3 + 0.7
      for (let i = 0; i < 4; i++) {
        const tx = (i * 220 + 80 - (scrollOffset.current * 0.5) % 220 + 220) % (GAME_WIDTH + 220) - 110
        ctx.fillStyle = `rgba(255, 140, 0, ${0.15 * flicker})`
        ctx.beginPath()
        ctx.arc(tx, 60, 40, 0, Math.PI * 2)
        ctx.fill()
        ctx.font = '20px serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'bottom'
        ctx.fillText('\u{1F525}', tx, 65)
      }
    }

    const drawGround = () => {
      // draw ground segments, skipping lava pits
      let segments = [{ start: 0, end: GAME_WIDTH }]

      pits.current.forEach((pit) => {
        const pitLeft = toScreen(pit.x)
        const pitRight = toScreen(pit.x + pit.width)
        if (pitRight < 0 || pitLeft > GAME_WIDTH) return
        const newSegments = []
        segments.forEach((seg) => {
          if (pitLeft >= seg.end || pitRight <= seg.start) {
            newSegments.push(seg)
          } else {
            if (seg.start < pitLeft) newSegments.push({ start: seg.start, end: pitLeft })
            if (pitRight < seg.end) newSegments.push({ start: pitRight, end: seg.end })
          }
        })
        segments = newSegments
      })

      // stone floor
      ctx.fillStyle = '#4a4a4a'
      segments.forEach((seg) => {
        ctx.fillRect(seg.start, GROUND_Y, seg.end - seg.start, GAME_HEIGHT - GROUND_Y)
      })
      // floor brick lines
      ctx.strokeStyle = '#333'
      ctx.lineWidth = 1
      segments.forEach((seg) => {
        for (let bx = seg.start; bx < seg.end; bx += 40) {
          ctx.strokeRect(bx, GROUND_Y, 40, GAME_HEIGHT - GROUND_Y)
        }
      })

      // lava pits
      pits.current.forEach((pit) => {
        const pitLeft = toScreen(pit.x)
        const pitRight = toScreen(pit.x + pit.width)
        if (pitRight < 0 || pitLeft > GAME_WIDTH) return

        // lava glow
        const lavaGrad = ctx.createLinearGradient(0, GROUND_Y, 0, GAME_HEIGHT)
        lavaGrad.addColorStop(0, '#ff4500')
        lavaGrad.addColorStop(0.5, '#ff6600')
        lavaGrad.addColorStop(1, '#cc3300')
        ctx.fillStyle = lavaGrad
        ctx.fillRect(pitLeft, GROUND_Y + 5, pitRight - pitLeft, GAME_HEIGHT - GROUND_Y - 5)

        // lava surface bubbles
        ctx.fillStyle = '#ffaa00'
        const bubbleOffset = (Date.now() / 200) % 20
        for (let bx = pitLeft + 10; bx < pitRight - 10; bx += 20) {
          const by = GROUND_Y + 10 + Math.sin(bx + bubbleOffset) * 3
          ctx.beginPath()
          ctx.arc(bx, by, 4, 0, Math.PI * 2)
          ctx.fill()
        }

        // pit edges
        ctx.fillStyle = '#333'
        ctx.fillRect(pitLeft, GROUND_Y, 4, GAME_HEIGHT - GROUND_Y)
        ctx.fillRect(pitRight - 4, GROUND_Y, 4, GAME_HEIGHT - GROUND_Y)
      })
    }

    const drawPlayer = (dead) => {
      const screenX = toScreen(playerX.current)
      ctx.font = '36px serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'bottom'
      ctx.save()
      ctx.translate(screenX, playerY.current)
      ctx.scale(-1, 1) // face right
      if (dead) ctx.rotate(Math.PI / 2)
      ctx.fillText('\u{1F3C3}', 0, 0)
      ctx.restore()
    }

    const drawHUD = () => {
      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 20px monospace'
      ctx.textAlign = 'right'
      ctx.textBaseline = 'top'
      const displayScore = Math.floor(scrollOffset.current / 10)
      ctx.fillText(`Distance: ${displayScore}`, GAME_WIDTH - 20, 35)

      if (jetpackTimer.current > 0) {
        const seconds = (jetpackTimer.current / 60).toFixed(1)
        ctx.textAlign = 'left'
        ctx.fillStyle = '#aa55ff'
        ctx.fillText(`\u{1F680} Jetpack: ${seconds}s`, 20, 35)
      }
    }

    const gameLoop = () => {
      if (gameStateRef.current !== 'running') {
        drawBackground()
        drawGround()
        drawPlayer(gameStateRef.current === 'dead')

        ctx.fillStyle = '#ffffff'
        ctx.font = 'bold 28px monospace'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        if (gameStateRef.current === 'idle') {
          ctx.fillText('Jet Jump', GAME_WIDTH / 2, GAME_HEIGHT / 2 - 50)
          ctx.font = '18px monospace'
          ctx.fillText('Press SPACE to start!', GAME_WIDTH / 2, GAME_HEIGHT / 2)
        } else {
          ctx.fillText('Game Over!', GAME_WIDTH / 2, GAME_HEIGHT / 2 - 50)
          ctx.font = '22px monospace'
          ctx.fillText(`Distance: ${Math.floor(scrollOffset.current / 10)}`, GAME_WIDTH / 2, GAME_HEIGHT / 2 - 10)
          ctx.fillText('Press SPACE to try again', GAME_WIDTH / 2, GAME_HEIGHT / 2 + 30)
        }
        frameRef.current = requestAnimationFrame(gameLoop)
        return
      }

      // scroll the world
      scrollOffset.current += SCROLL_SPEED
      playerX.current += SCROLL_SPEED

      // jetpack flying
      if (jetpackTimer.current > 0) {
        jetpackTimer.current -= 1
        velocityY.current = 0
        if (keysDown.current['ArrowUp']) {
          playerY.current -= JETPACK_FLY_SPEED
        }
        if (keysDown.current['ArrowDown']) {
          playerY.current += JETPACK_FLY_SPEED
        }
        // clamp to screen
        playerY.current = Math.max(40, Math.min(GROUND_Y, playerY.current))

        // when jetpack expires, remove any pit the player is over
        if (jetpackTimer.current === 0) {
          pits.current = pits.current.filter((pit) => {
            return !(playerX.current + 12 > pit.x + 8 && playerX.current - 12 < pit.x + pit.width - 8)
          })
          playerY.current = GROUND_Y
          isJumping.current = false
        }
      } else {
        // vertical physics
        velocityY.current += GRAVITY
        playerY.current += velocityY.current
      }

      if (playerY.current >= GROUND_Y) {
        // check if over a lava pit
        let overPit = false
        pits.current.forEach((pit) => {
          if (playerX.current + 12 > pit.x + 8 && playerX.current - 12 < pit.x + pit.width - 8) {
            overPit = true
          }
        })

        if (overPit) {
          if (playerY.current > GAME_HEIGHT + 20) {
            die()
          }
        } else {
          playerY.current = GROUND_Y
          velocityY.current = 0
          isJumping.current = false
        }
      }

      // player fell off left edge
      if (toScreen(playerX.current) < -50) {
        die()
      }

      // generate more pits ahead
      const furthestPit = pits.current.reduce(
        (max, p) => Math.max(max, p.x + p.width),
        0,
      )
      if (furthestPit < scrollOffset.current + GAME_WIDTH * 2) {
        pits.current.push(...generatePits(furthestPit))
      }
      // clean up pits far behind
      pits.current = pits.current.filter(
        (p) => p.x + p.width > scrollOffset.current - 200,
      )

      // portal collision and spawning (only when not jetpacking)
      if (jetpackTimer.current <= 0) {
        portals.current = portals.current.filter((portal) => {
          const dx = playerX.current - portal.x
          const dy = playerY.current - 18 - portal.y
          if (Math.sqrt(dx * dx + dy * dy) < PORTAL_RADIUS + 15) {
            jetpackTimer.current = JETPACK_DURATION
            velocityY.current = 0
            isJumping.current = true
            return false
          }
          return true
        })

        // generate more portals ahead
        const furthestPortal = portals.current.reduce(
          (max, p) => Math.max(max, p.x),
          scrollOffset.current,
        )
        if (furthestPortal < scrollOffset.current + GAME_WIDTH * 2) {
          portals.current.push(...generatePortals(furthestPortal))
        }
        // clean up portals behind
        portals.current = portals.current.filter(
          (p) => p.x > scrollOffset.current - 100,
        )
      }

      // move bullets
      bullets.current = bullets.current
        .map((b) => ({ ...b, x: b.x + b.vx, y: b.y + b.vy }))
        .filter((b) => b.x < scrollOffset.current + GAME_WIDTH + 100 && b.y < GAME_HEIGHT + 50)

      // zombies (only when jetpacking)
      if (jetpackTimer.current > 0) {
        // spawn zombies ahead on the ground
        if (zombies.current.length < 1) {
          const spawnX = scrollOffset.current + GAME_WIDTH + 50 + Math.random() * 200
          zombies.current.push({ x: spawnX, y: GROUND_Y, shootTimer: 30 + Math.floor(Math.random() * 60) })
        }

        // zombie AI: shoot boulders at player
        zombies.current.forEach((z) => {
          z.shootTimer -= 1
          if (z.shootTimer <= 0) {
            const dx = playerX.current - z.x
            const dy = (playerY.current - 18) - z.y
            const dist = Math.sqrt(dx * dx + dy * dy)
            if (dist > 0) {
              boulders.current.push({
                x: z.x,
                y: z.y,
                vx: (dx / dist) * BOULDER_SPEED,
                vy: (dy / dist) * BOULDER_SPEED,
              })
            }
            z.shootTimer = ZOMBIE_SHOOT_INTERVAL
          }
        })

        // remove zombies that scrolled off left
        zombies.current = zombies.current.filter((z) => z.x > scrollOffset.current - 60)

        // check bullet hits on zombies
        zombies.current = zombies.current.filter((z) => {
          const hit = bullets.current.some(
            (b) => Math.abs(b.x - z.x) < 25 && Math.abs(b.y - z.y) < 25,
          )
          if (hit) {
            bullets.current = bullets.current.filter(
              (b) => !(Math.abs(b.x - z.x) < 25 && Math.abs(b.y - z.y) < 25),
            )
            smokes.current.push({ x: z.x, y: z.y, timer: 30 })
          }
          return !hit
        })
      } else {
        // clear zombies when no jetpack
        zombies.current = []
        boulders.current = []
      }

      // move boulders
      boulders.current = boulders.current
        .map((b) => ({ ...b, x: b.x + b.vx, y: b.y + b.vy }))
        .filter((b) => {
          const sx = b.x - scrollOffset.current
          return sx > -50 && sx < GAME_WIDTH + 50 && b.y > -50 && b.y < GAME_HEIGHT + 50
        })

      // boulder hits player
      boulders.current.forEach((b) => {
        if (Math.abs(b.x - playerX.current) < 20 && Math.abs(b.y - (playerY.current - 18)) < 20) {
          die()
        }
      })

      // tick smoke effects
      smokes.current = smokes.current
        .map((s) => ({ ...s, timer: s.timer - 1 }))
        .filter((s) => s.timer > 0)

      // draw
      drawBackground()
      drawGround()

      // draw portals (hidden while jetpacking)
      if (jetpackTimer.current <= 0) portals.current.forEach((portal) => {
        const sx = toScreen(portal.x)
        if (sx < -40 || sx > GAME_WIDTH + 40) return
        // swirling portal effect
        const t = Date.now() / 300
        ctx.save()
        ctx.translate(sx, portal.y)
        ctx.rotate(t)
        ctx.beginPath()
        ctx.ellipse(0, 0, PORTAL_RADIUS, PORTAL_RADIUS * 0.7, 0, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(138, 43, 226, 0.6)'
        ctx.fill()
        ctx.beginPath()
        ctx.ellipse(0, 0, PORTAL_RADIUS * 0.6, PORTAL_RADIUS * 0.4, 0, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(200, 100, 255, 0.8)'
        ctx.fill()
        ctx.restore()
        // sparkles
        ctx.font = '14px serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText('\u{2728}', sx + Math.sin(t * 2) * 15, portal.y + Math.cos(t * 2) * 10)
      })

      drawPlayer(false)

      // draw jetpack flames
      if (jetpackTimer.current > 0) {
        const screenX = toScreen(playerX.current)
        const flameFlicker = Math.random() * 4
        ctx.font = '18px serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'top'
        ctx.fillText('\u{1F525}', screenX + 8, playerY.current - 5 + flameFlicker)
        ctx.fillText('\u{1F525}', screenX - 8, playerY.current - 3 + flameFlicker)

        // gun and aim line
        const gunX = screenX + 15
        const gunY = playerY.current - 20
        ctx.font = '16px serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.save()
        ctx.translate(gunX, gunY)
        ctx.scale(-1, 1)
        ctx.fillText('\u{1F52B}', 0, 0)
        ctx.restore()

        // dotted trajectory line
        ctx.strokeStyle = 'rgba(255, 255, 100, 0.5)'
        ctx.lineWidth = 1
        ctx.setLineDash([5, 8])
        ctx.beginPath()
        ctx.moveTo(gunX + 10, gunY)
        let lx = gunX + 10
        let ly = gunY
        for (let i = 0; i < 20; i++) {
          lx += BULLET_SPEED * 0.7
          ly += 2 * 0.7
          ctx.lineTo(lx, ly)
        }
        ctx.stroke()
        ctx.setLineDash([])
      }

      // draw zombies
      zombies.current.forEach((z) => {
        const zx = toScreen(z.x)
        if (zx < -40 || zx > GAME_WIDTH + 40) return
        ctx.font = '34px serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'bottom'
        ctx.fillText('\u{1F9DF}', zx, z.y)
      })

      // draw boulders
      boulders.current.forEach((b) => {
        const bx = toScreen(b.x)
        if (bx < -20 || bx > GAME_WIDTH + 20) return
        ctx.font = '20px serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText('\u{1FAA8}', bx, b.y)
      })

      // draw smoke effects
      smokes.current.forEach((s) => {
        const sx = toScreen(s.x)
        const opacity = s.timer / 30
        const size = 30 + (1 - opacity) * 20
        ctx.globalAlpha = opacity
        ctx.font = `${Math.floor(size)}px serif`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText('\u{1F4A8}', sx, s.y)
        ctx.globalAlpha = 1
      })

      // draw bullets
      bullets.current.forEach((b) => {
        const bx = toScreen(b.x)
        if (bx < -10 || bx > GAME_WIDTH + 10) return
        ctx.fillStyle = '#ffff44'
        ctx.beginPath()
        ctx.arc(bx, b.y, 4, 0, Math.PI * 2)
        ctx.fill()
        // bullet trail
        ctx.strokeStyle = 'rgba(255, 255, 100, 0.4)'
        ctx.lineWidth = 2
        ctx.beginPath()
        ctx.moveTo(bx, b.y)
        ctx.lineTo(bx - b.vx * 2, b.y - b.vy * 2)
        ctx.stroke()
      })

      drawHUD()

      frameRef.current = requestAnimationFrame(gameLoop)
    }

    frameRef.current = requestAnimationFrame(gameLoop)
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current)
    }
  }, [die, gameState])

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      <canvas
        ref={canvasRef}
        width={GAME_WIDTH}
        height={GAME_HEIGHT}
        style={{
          border: '2px solid #555',
          borderRadius: 8,
          cursor: 'pointer',
        }}
        onClick={() => {
          if (gameStateRef.current !== 'running') {
            startGame()
          } else if (jetpackTimer.current <= 0 && !isJumping.current) {
            velocityY.current = JUMP_FORCE
            isJumping.current = true
          }
        }}
      />
      {highScore > 0 && (
        <Box sx={{ color: 'text.secondary', fontSize: '1.2em' }}>
          High Score: {highScore}
        </Box>
      )}
      <Box sx={{ color: 'text.secondary', fontSize: '0.9em' }}>
        SPACE or click to jump | Fly through portals for jetpack!
      </Box>
    </Box>
  )
}

export default JetJump
